import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { generateSEOAltText } from '@/utils/seoAltTextGenerator';

interface PerformanceOptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
  loading?: 'lazy' | 'eager';
  gameType?: string;
  serverName?: string;
  imageType?: 'logo' | 'banner' | 'avatar' | 'category';
  width?: number;
  height?: number;
  priority?: boolean;
  sizes?: string; // Nouveau: pour srcset responsive personnalisé
}

export const PerformanceOptimizedImage: React.FC<PerformanceOptimizedImageProps> = ({
  src,
  alt,
  className,
  fallback,
  loading = 'lazy',
  gameType,
  serverName,
  imageType = 'logo',
  width,
  height,
  priority = false,
  sizes // Nouveau prop
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(priority);
  const imageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setHasError(false);
    setIsLoaded(false);
    setIsIntersecting(priority);
  }, [src, priority]);

  // Intersection Observer pour le lazy loading manuel
  useEffect(() => {
    if (priority) {
      setIsIntersecting(true);
      return;
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsIntersecting(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      { 
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    } else {
      setIsIntersecting(true);
      observer.disconnect();
    }

    return () => observer.disconnect();
  }, [priority, src]);

  const handleError = () => {
    setHasError(true);
    setIsLoaded(false);
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const { language } = useLanguage();

  // Générer un alt tag SEO optimisé et multilingue
  const generateOptimizedAlt = () => {
    if (alt && alt !== '') return alt;
    
    // Utiliser le générateur SEO pour créer des alt texts optimisés
    if (imageType === 'logo' && serverName) {
      return generateSEOAltText({
        type: 'server-logo',
        name: serverName,
        gameName: gameType,
        language
      });
    } else if (imageType === 'banner' && serverName) {
      return generateSEOAltText({
        type: 'server-banner',
        name: serverName,
        gameName: gameType,
        language
      });
    } else if (imageType === 'category' && gameType) {
      return generateSEOAltText({
        type: 'game-logo',
        name: gameType,
        language
      });
    } else if (imageType === 'avatar' && serverName) {
      return generateSEOAltText({
        type: 'user-avatar',
        name: serverName,
        language
      });
    }
    
    // Fallback pour les cas non gérés
    return serverName || gameType || language === 'en' 
      ? 'Gaming server image' 
      : language === 'es' 
        ? 'Imagen servidor gaming' 
        : 'Image serveur gaming';
  };

  // Générer les URLs optimisées pour différents formats avec compression agressive
  const getOptimizedSrc = () => {
    if (!src) return '';
    const isSupabaseStorage = src.includes('supabase.co/storage');
    const outputFormat = src.endsWith('.avif') ? 'avif' : 'webp';
    
    // Optimisation Supabase même pour les images déjà en WebP/AVIF
    if (isSupabaseStorage) {
      const url = new URL(src);
      
      // Dimensions optimales selon le type d'image
      const optimalSizes = {
        logo: { width: 120, height: 120 },
        banner: { width: 1200, height: 400 },
        avatar: { width: 80, height: 80 },
        category: { width: 800, height: 450 }
      };
      
      const targetSize = optimalSizes[imageType] || { width: width || 800, height: height || 600 };
      
      url.searchParams.set('width', targetSize.width.toString());
      url.searchParams.set('height', targetSize.height.toString());
      url.searchParams.set('quality', '55'); // Compression ultra-agressive
      url.searchParams.set('format', outputFormat); // Format moderne conservé
      url.searchParams.set('resize', 'contain');
      
      return url.toString();
    }
    
    return src;
  };

  // Générer srcset pour images responsives
  const getSrcSet = (format: 'avif' | 'webp' = 'webp') => {
    if (!src || !src.includes('supabase.co/storage')) return undefined;
    
    const responsiveSizes = imageType === 'logo' ? [60, 120, 240] : 
                            imageType === 'avatar' ? [40, 80, 160] :
                            imageType === 'banner' ? [480, 800, 1200] :
                            [400, 800, 1200];
    
    return responsiveSizes.map(size => {
      const url = new URL(src);
      url.searchParams.set('width', size.toString());
      if (imageType === 'logo' || imageType === 'avatar') {
        url.searchParams.set('height', size.toString());
      }
      url.searchParams.set('quality', '55'); // Compression ultra-agressive
      url.searchParams.set('format', format);
      url.searchParams.set('resize', 'contain');
      return `${url.toString()} ${size}w`;
    }).join(', ');
  };

  const fallbackElement = fallback || (
    <div className={cn(
      'flex items-center justify-center bg-slate-800 text-slate-500',
      className
    )}>
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
      </svg>
    </div>
  );

  if (hasError) {
    return <>{fallbackElement}</>;
  }

  return (
    <div ref={imageRef} className="relative" data-image-src={src}>
      {/* Placeholder pendant le chargement */}
      {!isLoaded && (
        <div className={cn(
          'absolute inset-0 flex items-center justify-center bg-slate-800 animate-pulse',
          className
        )}>
          <div className="w-6 h-6 bg-slate-600 rounded animate-pulse"></div>
        </div>
      )}
      
      {/* Image principale avec compression et lazy loading optimisé */}
      {isIntersecting && (
        <picture>
          {/* Format AVIF pour navigateurs modernes (meilleure compression) */}
          {src.includes('supabase.co/storage') && (
            <source 
              type="image/avif"
              srcSet={getSrcSet('avif')}
              sizes={sizes || (imageType === 'logo' ? '120px' : 
                     imageType === 'avatar' ? '80px' :
                     imageType === 'banner' ? '(max-width: 768px) 92vw, 672px' : 
                     '(max-width: 768px) 100vw, 50vw')}

            />
          )}
          
          {/* Format WebP en fallback */}
          {src.includes('supabase.co/storage') && (
            <source 
              type="image/webp"
              srcSet={getSrcSet('webp')}
              sizes={sizes || (imageType === 'logo' ? '120px' : 
                     imageType === 'avatar' ? '80px' :
                     imageType === 'banner' ? '(max-width: 768px) 92vw, 672px' : 
                     '(max-width: 768px) 100vw, 50vw')}

            />
          )}
          
          {/* Image originale en dernier fallback */}
          <img
            src={getOptimizedSrc()}
            alt={generateOptimizedAlt()}
            className={cn(
              className, 
              'transition-opacity duration-300',
              { 'opacity-0': !isLoaded, 'opacity-100': isLoaded }
            )}
            loading={priority ? 'eager' : loading}
            fetchPriority={priority ? 'high' : 'auto'}
            onError={handleError}
            onLoad={handleLoad}
            decoding="async"
            width={width || (imageType === 'logo' ? '120' : imageType === 'avatar' ? '80' : imageType === 'banner' ? '1200' : '800')}
            height={height || (imageType === 'logo' ? '120' : imageType === 'avatar' ? '80' : imageType === 'banner' ? '400' : '600')}
            title={generateOptimizedAlt()}
            style={{ aspectRatio: width && height ? `${width}/${height}` : undefined }}
          />
        </picture>
      )}
    </div>
  );
};